import { store } from '@/app/store'
import { syncCanvasState } from '@/features/adp/state/canvas/canvasSlice'
import { openOverlay } from '@/features/adp/state/overlaySlice'
import { useMutation } from '@tanstack/react-query'
import { mergeAdpSessionNode } from './useAdpSessionStore'
import { useTargetData } from './useTargetData'
import type { RegisteredDatabase, RegisteredTargetTable } from '@/features/adp/services/api-service.types'
import type { PersistedDestinationConfigurationState } from '@/features/adp/components/destination/sheets/DestinationConfiguration'
import type { LoadTypeFormData } from '@/features/adp/schemas/LoadType'

/**
 * Inputs for finalizing the registered-destination flow (Table List Save).
 *
 * Mirrors the registered-source finalize mutation: persists target session
 * data, marks the canvas node configured, and opens Column Mapping
 * directly — skipping LoadType / Output Preview because the target table
 * already exists server-side.
 */
export type FinalizeRegisteredDestinationParams = {
  nodeId: string
  targetDataSourceId: number
  /** The selected existing target table on the registered destination. */
  table: RegisteredTargetTable
  /** `databases[0]` from `getRegisteredTables` — supplies type / data_source_type / host / port / etc. */
  database: RegisteredDatabase
}

/**
 * Backend rejects these on `/connections/columns` style requests; we strip
 * them mirroring what the source-side does. `bucket_name`, `warehouse`, etc.
 * pass through untouched so non-RDBMS destinations work.
 */
const REJECTED_PAYLOAD_KEYS = new Set(['database_name', 'endpoint', 'secret_key2'])

/**
 * Build the connection payload persisted as
 * `destinationConfigurationState.submitted_destination_payload`. Same pass-
 * through-minus-rejected pattern the source flow uses. `port` is coerced to
 * a number because the backend's `GetColumnsDatabaseDto` validation requires it.
 */
function buildSubmittedDestinationPayload(
  database: RegisteredDatabase,
  tableName: string,
): Record<string, unknown> {
  const raw = database as unknown as Record<string, unknown>
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(raw)) {
    if (REJECTED_PAYLOAD_KEYS.has(k)) continue
    if (v === '' || v === null || v === undefined) continue
    if (k === 'image_link' || k === 'data_source_instance_label' || k === 'data_source_type') continue
    out[k] = v
  }
  if (database.port) {
    const port = Number(database.port)
    if (Number.isFinite(port) && port > 0) {
      out.port = port
    }
  }
  out.type = database.type
  out.source_name = database.source_name
  // `table_name` is required inside each `databases[]` object per Swagger's
  // PhysicalTableDatabaseDto. This path goes straight to Column Mapping (no
  // `/physical-tables` call), but keeping the payload spec-correct future-
  // proofs a revisit that routes through OutputPreview.
  out.table_name = tableName
  return out
}

/**
 * Map the registered table's fields → `target_columns` shape that
 * `ColumnMapping` reads (`{ column_name, column_data_type }`). Adds
 * `target_table_column_id` / `target_data_source_id` / `target_table_id` so
 * downstream consumers (output preview replay, save payload builders) see
 * the same shape they'd get from a fresh create-target-table-batch response.
 */
function buildTargetColumnsFromTable(
  table: RegisteredTargetTable,
  targetDataSourceId: number,
) {
  return table.fields.map(field => ({
    target_table_column_id: field.target_table_column_id,
    target_data_source_id: targetDataSourceId,
    target_table_id: table.target_table_id,
    column_name: field.column_name,
    column_data_type: field.data_type,
  }))
}

/**
 * Sensible defaults for `loadTypeForm` when reusing an existing registered
 * target. The codes on `RegisteredTargetTable.load_type_code` /
 * `scd_type_code` aren't mapped client-side today; backend reads its own
 * persisted values for the existing table so the in-session form is mostly
 * informational. Defaults match what new-destination LoadType seeds.
 */
function defaultLoadTypeForm(): LoadTypeFormData {
  return {
    loadType: 'full',
    scdType: 'SCD1',
    deleteIdentifier: '',
    createdDate: '',
    modifiedDate: '',
  }
}

type FinalizeResult = void

/**
 * Wraps the post-save side effects for the registered-destination flow:
 *   - Persist `target_data` with `target_table` + `target_columns` so the
 *     destination revisit short-circuit in OutputPreview sees `isAlreadyCreated`.
 *   - Mark the canvas destination node configured (`mode: 'existing-destination'`).
 *   - Persist destinationConfigurationState as readonly so DestinationConfiguration
 *     shows the "Continue" button on revisit.
 *   - Open Column Mapping (target already exists; skip LoadType / Output Preview).
 */
export function useFinalizeRegisteredDestinationMutation() {
  const { setTargetData } = useTargetData()

  return useMutation<FinalizeResult, Error, FinalizeRegisteredDestinationParams>({
    mutationFn: async () => {
      // No async work — the registered table already exists server-side. The
      // mutation wrapper is kept for parity with the source-side hook and to
      // give callers a single .mutate handle with consistent loading state.
    },
    onSuccess: (_result, params) => {
      const dispatch = store.dispatch
      const { nodeId, targetDataSourceId, table, database } = params

      const targetColumns = buildTargetColumnsFromTable(table, targetDataSourceId)
      const dataSourceInstance = database.type ?? ''
      const dataSourceType = database.data_source_type ?? null

      setTargetData(nodeId, {
        target_data_source_id: targetDataSourceId,
        source_name: database.source_name,
        table_name: table.table_name,
        path: '',
        database: database.database,
        schema: database.schema || null,
        data_source_instance: dataSourceInstance,
        data_source_type: dataSourceType,
        // Single-element `target_table` so OutputPreview's revisit detection
        // (`Array.isArray(target_table) && target_table.length > 0`) treats
        // this destination as already-created — clicking "Continue" later
        // skips both POSTs cleanly.
        target_table: [
          {
            target_data_source_id: targetDataSourceId,
            target_table_id: table.target_table_id,
            table_name: table.table_name,
            data_source_name: table.data_source_name,
          },
        ],
        target_columns: targetColumns,
        loadTypeForm: defaultLoadTypeForm(),
      })

      const submittedDestinationPayload = buildSubmittedDestinationPayload(
        database,
        table.table_name,
      )
      mergeAdpSessionNode(nodeId, {
        destinationConfigurationState: {
          isConfigured: true,
          isReadOnly: true,
          values: {
            source_name: database.source_name ?? '',
            table_name: table.table_name ?? '',
            host: database.host ?? '',
            port: database.port ?? '',
            database: database.database ?? '',
            username: database.username ?? '',
            password: database.password ?? '',
            schema: database.schema ?? '',
            secret_key: database.secret_key ?? '',
          },
          schemaFields: [],
          postTestFields: [],
          submitted_destination_payload: submittedDestinationPayload as PersistedDestinationConfigurationState['submitted_destination_payload'],
          data_source_instance: dataSourceInstance,
          data_source_type: dataSourceType,
        } satisfies PersistedDestinationConfigurationState,
      })

      const snapshot = store.getState()
      const nextNodes = [...snapshot.canvas.present.nodes]
      const destinationNodeIndex = nextNodes.findIndex(
        node => node.id === nodeId && node.type === 'destination',
      )
      if (destinationNodeIndex >= 0) {
        const destinationNode = nextNodes[destinationNodeIndex]
        nextNodes[destinationNodeIndex] = {
          ...destinationNode,
          data: {
            ...destinationNode.data,
            nodeSecondaryTitle: database.source_name ?? destinationNode.data?.nodeSecondaryTitle,
            mode: 'existing-destination',
          },
        } as typeof destinationNode
        dispatch(
          syncCanvasState({
            nodes: nextNodes,
            edges: snapshot.canvas.present.edges,
          }),
        )
      }

      dispatch(
        openOverlay({
          type: 'column-mapping',
          data: { nodeId },
        }),
      )
    },
  })
}
